import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  LinearProgress,
  Alert,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Typography,
  Chip,
  TablePagination
} from "@mui/material";
import NavigationLayout from './NavigationLayout';

const MainMenu = () => {
  const [openPDUDialog, setOpenPDUDialog] = useState(false);
  const [selectedPDU, setSelectedPDU] = useState("");
  const [smsData, setSmsData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalRecords, setTotalRecords] = useState(0);

  const token = localStorage.getItem("authToken");

  const fetchData = useCallback(async () => {
    if (!token) return;

    try {
      setLoading(true);
      const baseUrl = process.env.REACT_APP_API_URL || "https://toolbox.msgg.me/api/messages";
      const apiUrl = `${baseUrl}?page=${page + 1}&limit=${rowsPerPage}`;

      const response = await fetch(apiUrl, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = await response.json();

      if (data.messages && data.pagination) {
        setSmsData(data.messages);
        setTotalRecords(data.pagination.totalRecords);
      } else if (Array.isArray(data)) {
        setSmsData(data);
        setTotalRecords(data.length);
      }

      setError("");
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token, page, rowsPerPage]);

  useEffect(() => {
    fetchData();
    const intervalId = setInterval(fetchData, 10000);
    return () => clearInterval(intervalId);
  }, [fetchData]);

  const handlePDUClick = (pdu) => {
    setSelectedPDU(pdu);
    setOpenPDUDialog(true);
  };

  return (
    <NavigationLayout title="Msgg.me Toolbox">
      <Container maxWidth="xl" sx={{ mt: 2, mb: 4, px: { xs: 1, sm: 2, md: 3 } }}>
        {token ? (
          <>
            {loading && <LinearProgress sx={{ mb: 2 }} />}
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            {smsData.length > 0 ? (
              <Paper elevation={3} sx={{ width: '100%', overflowX: 'auto' }}>
                <Table sx={{ minWidth: 1000 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ minWidth: '120px' }}><strong>PHONE ID</strong></TableCell>
                      <TableCell sx={{ minWidth: '160px' }}><strong>DATETIME</strong></TableCell>
                      <TableCell sx={{ minWidth: '140px' }}><strong>FROM</strong></TableCell>
                      <TableCell sx={{ minWidth: '140px' }}><strong>TO</strong></TableCell>
                      <TableCell sx={{ minWidth: '130px' }}><strong>SMSC</strong></TableCell>
                      <TableCell sx={{ minWidth: '350px' }}><strong>CONTENT</strong></TableCell>
                      <TableCell sx={{ minWidth: '110px' }}><strong>PDU</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {smsData.map((row, i) => (
                      <TableRow key={i}>
                        <TableCell>
                          <Chip 
                            label={row.PHONE_ID || "N/A"} 
                            size="small" 
                            variant="outlined" 
                            color="primary" 
                          />
                        </TableCell>
                        <TableCell>{row.DATETIME}</TableCell>
                        <TableCell>{row.FROM}</TableCell>
                        <TableCell>{row.TO}</TableCell>
                        <TableCell>{row.SMSC}</TableCell>
                        <TableCell sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                          {row.CONTENT?.replace(/"/g, "")}
                        </TableCell>
                        <TableCell>
                          <Button size="small" onClick={() => handlePDUClick(row.PDU || "")}>
                            View PDU
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>

                <TablePagination
                  component="div"
                  count={totalRecords}
                  page={page}
                  onPageChange={(e, newPage) => setPage(newPage)}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={(e) => {
                    setRowsPerPage(parseInt(e.target.value, 10));
                    setPage(0);
                  }}
                  rowsPerPageOptions={[10, 25, 50, 100]}
                />
              </Paper>
            ) : (
              !loading && <Alert severity="info">No SMS records found for your assigned numbers.</Alert>
            )}
          </>
        ) : (
          <Alert severity="error" sx={{ mt: 4 }}>
            You must log in to view this page. Please go back to the login screen.
          </Alert>
        )}
      </Container>

      {/* PDU Viewer Modal */}
      <Dialog open={openPDUDialog} onClose={() => setOpenPDUDialog(false)} maxWidth="lg" fullWidth>
        <DialogTitle>PDU Content</DialogTitle>
        <DialogContent>
          <Typography sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word", fontSize: "14px" }}>
            {selectedPDU.replace(/"/g, "")}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenPDUDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </NavigationLayout>
  );
};

export default MainMenu;